import { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { cn } from '@/lib/utils';
import {
    useFirstCaptureConversion,
    type FirstCaptureConversionPoint,
    type IncomeGranularity,
} from '@/hooks/useAdminAnalytics';

const LINE_COLOR = 'hsl(142, 71%, 45%)';

const tooltipStyle = {
    background: 'hsl(222 47% 9%)',
    border: '1px solid hsl(222 30% 16%)',
    borderRadius: '8px',
    fontSize: '12px',
};

const GRANULARITY_OPTIONS: { value: IncomeGranularity; label: string }[] = [
    { value: 'day', label: 'Día' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' },
];

const NOTE =
    'De los usuarios registrados en cada periodo, porcentaje que ha publicado al menos una captura (sin borradores), contado hasta ayer. Los periodos recientes pueden salir más bajos: aún no han tenido tiempo de registrar la primera.';

interface FirstCaptureConversionChartProps {
    days: number | null;
    isMobile?: boolean;
}

function formatPeriodLabel(iso: string, granularity: IncomeGranularity, withYear = false): string {
    const [year, month, day] = iso.slice(0, 10).split('-');
    if (!year || !month || !day) return iso;

    if (granularity === 'month') {
        const d = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
        return d.toLocaleDateString('es-ES', {
            month: 'short',
            year: 'numeric',
            timeZone: 'UTC',
        });
    }

    if (granularity === 'week') {
        return withYear
            ? `Sem ${Number(day)}/${Number(month)}/${year}`
            : `Sem ${Number(day)}/${Number(month)}`;
    }

    return withYear ? `${Number(day)}/${Number(month)}/${year}` : `${Number(day)}/${Number(month)}`;
}

function formatPct(value: number, digits = 1): string {
    return `${value.toLocaleString('es-ES', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    })} %`;
}

function periodRate(rows: FirstCaptureConversionPoint[]): { pct: number; registered: number; firstCapture: number } {
    const registered = rows.reduce((acc, row) => acc + row.registered, 0);
    const firstCapture = rows.reduce((acc, row) => acc + row.firstCapture, 0);
    const pct = registered > 0 ? (100 * firstCapture) / registered : 0;
    return { pct, registered, firstCapture };
}

export function FirstCaptureConversionChart({ days, isMobile = false }: FirstCaptureConversionChartProps) {
    const [granularity, setGranularity] = useState<IncomeGranularity>('week');
    const { data, isLoading, error } = useFirstCaptureConversion(days, granularity);

    const chartData = useMemo(
        () =>
            (data ?? []).map((row) => ({
                periodStart: row.periodStart,
                conversionPct: row.conversionPct,
                registered: row.registered,
                firstCapture: row.firstCapture,
            })),
        [data],
    );

    const summary = useMemo(() => periodRate(data ?? []), [data]);

    return (
        <div className="space-y-3 min-w-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-foreground">Registro → primera captura</h3>
                <p className="text-xs text-muted-foreground tabular-nums shrink-0">
                    {isLoading
                        ? '—'
                        : `${formatPct(summary.pct)} en el periodo · ${summary.firstCapture.toLocaleString('es-ES')} de ${summary.registered.toLocaleString('es-ES')}`}
                </p>
            </div>

            <div className="flex flex-wrap gap-1 w-full min-w-0">
                {GRANULARITY_OPTIONS.map((opt) => (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => setGranularity(opt.value)}
                        className={cn(
                            'shrink-0 px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                            granularity === opt.value
                                ? 'bg-muted text-foreground'
                                : 'text-muted-foreground hover:text-foreground',
                        )}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            <p className="text-[11px] text-muted-foreground">{NOTE}</p>

            <div className="rounded-xl border border-border bg-card p-3 sm:p-5 min-w-0">
                <div className={isMobile ? 'h-[280px]' : 'h-[400px]'}>
                    {isLoading ? (
                        <div className="h-full flex items-center justify-center text-muted-foreground">
                            <RefreshCw size={20} className="animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="h-full flex items-center justify-center text-sm text-rose-500 px-4 text-center">
                            {(error as Error).message || 'No se pudo cargar la conversión a primera captura.'}
                        </div>
                    ) : chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={chartData}
                                margin={{
                                    top: 8,
                                    right: isMobile ? 4 : 8,
                                    bottom: isMobile ? 8 : 4,
                                    left: isMobile ? 0 : -4,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 30% 16%)" />
                                <XAxis
                                    dataKey="periodStart"
                                    tick={{ fontSize: isMobile ? 10 : 11, fill: 'hsl(215 20% 55%)' }}
                                    tickLine={false}
                                    axisLine={false}
                                    minTickGap={isMobile ? 36 : 24}
                                    interval="preserveStartEnd"
                                    tickFormatter={(val: string) =>
                                        formatPeriodLabel(String(val ?? ''), granularity)
                                    }
                                />
                                <YAxis
                                    width={isMobile ? 36 : 44}
                                    tick={{ fontSize: isMobile ? 10 : 11, fill: 'hsl(215 20% 55%)' }}
                                    tickLine={false}
                                    axisLine={false}
                                    domain={[0, 100]}
                                    ticks={[0, 25, 50, 75, 100]}
                                    tickFormatter={(value: number) => `${value}%`}
                                />
                                <Tooltip
                                    contentStyle={tooltipStyle}
                                    labelStyle={{ color: 'hsl(210 40% 98%)' }}
                                    labelFormatter={(val) =>
                                        formatPeriodLabel(String(val ?? ''), granularity, true)
                                    }
                                    formatter={(value: number, _name, item) => {
                                        const row = item?.payload as {
                                            firstCapture?: number;
                                            registered?: number;
                                        } | undefined;
                                        const first = row?.firstCapture ?? 0;
                                        const registered = row?.registered ?? 0;
                                        return [
                                            `${formatPct(Number(value) || 0)} · ${first.toLocaleString('es-ES')} de ${registered.toLocaleString('es-ES')}`,
                                            '1ª captura',
                                        ];
                                    }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="conversionPct"
                                    stroke={LINE_COLOR}
                                    strokeWidth={2}
                                    dot={chartData.length < 24}
                                    name="1ª captura"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                            No hay registros en el rango seleccionado
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
